import React from "react";
import type { Player, Position } from "../../../shared/models/Player";
import Headshot from "../../../shared/components/Rankings/Headshot";
import type { SearchMatch } from "../../../shared/components/Rankings/searchUtils";
import { DESKTOP_CONFIGURABLE_RANKING_TABS, type DesktopSourceTabVisibility } from "./rankingsListUtils";
import type { RankingsListKey } from "../../../shared/utils/xlsxRankings";

type RankingTabDescriptor = {
  key: RankingsListKey;
  ariaLabel: string;
  labelTop: string;
  labelBottom: string;
};

export function RankingsSettingsModal(props: {
  desktopRankingsSettingsOpen: boolean;
  desktopRankingsSettingsRef: React.RefObject<HTMLDivElement | null>;
  setDesktopRankingsSettingsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  confirmRestartDraftOpen: boolean;
  setConfirmRestartDraftOpen: React.Dispatch<React.SetStateAction<boolean>>;
  confirmRemoveExpertOpen: boolean;
  setConfirmRemoveExpertOpen: React.Dispatch<React.SetStateAction<boolean>>;
  expertRankingIds: string[];
  hideDraftedPlayers: boolean;
  setHideDraftedPlayers: React.Dispatch<React.SetStateAction<boolean>>;
  hideKickers: boolean;
  setHideKickers: React.Dispatch<React.SetStateAction<boolean>>;
  hideDefenses: boolean;
  setHideDefenses: React.Dispatch<React.SetStateAction<boolean>>;
  rankingTabs: RankingTabDescriptor[];
  desktopSourceTabVisibility: DesktopSourceTabVisibility;
  setDesktopSourceTabVisibility: React.Dispatch<React.SetStateAction<DesktopSourceTabVisibility>>;
  restartDraft: () => void;
  onRemoveExpertRankings?: () => void;
}) {
  const {
    desktopRankingsSettingsOpen,
    desktopRankingsSettingsRef,
    setDesktopRankingsSettingsOpen,
    confirmRestartDraftOpen,
    setConfirmRestartDraftOpen,
    confirmRemoveExpertOpen,
    setConfirmRemoveExpertOpen,
    expertRankingIds,
    hideDraftedPlayers,
    setHideDraftedPlayers,
    hideKickers,
    setHideKickers,
    hideDefenses,
    setHideDefenses,
    rankingTabs,
    desktopSourceTabVisibility,
    setDesktopSourceTabVisibility,
    restartDraft,
    onRemoveExpertRankings,
  } = props;

  if (!desktopRankingsSettingsOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(4,7,18,0.46)",
        backdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onPointerDown={() => setDesktopRankingsSettingsOpen(false)}
    >
      <div
        ref={desktopRankingsSettingsRef}
        style={{
          width: 380,
          maxWidth: "min(380px, calc(100vw - 40px))",
          borderRadius: 16,
          border: "1px solid rgba(255,255,255,0.12)",
          background: "rgba(12,18,34,0.98)",
          boxShadow: "0 18px 38px rgba(0,0,0,0.38)",
          padding: 14,
          display: "grid",
          gap: 12,
        }}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => setConfirmRestartDraftOpen(true)}
              style={pillDangerStyle}
            >
              Restart Draft
            </button>
            {expertRankingIds.length > 0 ? (
              <button
                type="button"
                onClick={() => setConfirmRemoveExpertOpen(true)}
                style={pillDangerStyle}
              >
                Remove Expert
              </button>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => setDesktopRankingsSettingsOpen(false)}
            style={closeButtonStyle}
          >
            ×
          </button>
        </div>

        <div style={sectionLabelStyle}>Rankings Settings</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[
            { hideLabel: "Hide Drafted", showLabel: "Show Drafted", active: hideDraftedPlayers, onClick: () => setHideDraftedPlayers((prev) => !prev) },
            { hideLabel: "Hide Kicker", showLabel: "Show Kicker", active: hideKickers, onClick: () => setHideKickers((prev) => !prev) },
            { hideLabel: "Hide Defense", showLabel: "Show Defense", active: hideDefenses, onClick: () => setHideDefenses((prev) => !prev) },
          ].map((option) => (
            <button
              key={option.hideLabel}
              type="button"
              onClick={option.onClick}
              style={option.active ? pillSelectedStyle : pillStyle}
            >
              {option.active ? option.showLabel : option.hideLabel}
            </button>
          ))}
        </div>

        <div style={sectionLabelStyle}>Tabs Settings</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {DESKTOP_CONFIGURABLE_RANKING_TABS.map((key) => {
            const tab = rankingTabs.find((item) => item.key === key);
            if (!tab) return null;
            const active = desktopSourceTabVisibility[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() =>
                  setDesktopSourceTabVisibility((prev) => ({
                    ...prev,
                    [key]: !prev[key],
                  }))
                }
                style={active ? pillSelectedStyle : pillStyle}
              >
                {active ? `Hide ${tab.ariaLabel}` : `Show ${tab.ariaLabel}`}
              </button>
            );
          })}
        </div>

        {confirmRestartDraftOpen ? (
          <div style={overlayStyle} onPointerDown={() => setConfirmRestartDraftOpen(false)}>
            <div style={confirmCardStyle} onPointerDown={(event) => event.stopPropagation()}>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: "#fff" }}>Restart Draft?</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.68)", lineHeight: 1.4 }}>
                  This will clear every drafted player from the current board.
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <button type="button" onClick={() => setConfirmRestartDraftOpen(false)} style={pillStyle}>Cancel</button>
                <button
                  type="button"
                  onClick={() => {
                    restartDraft();
                    setConfirmRestartDraftOpen(false);
                    setDesktopRankingsSettingsOpen(false);
                  }}
                  style={pillDangerStyle}
                >
                  Restart Draft
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {confirmRemoveExpertOpen ? (
          <div style={overlayStyle} onPointerDown={() => setConfirmRemoveExpertOpen(false)}>
            <div style={{ ...confirmCardStyle, maxWidth: 320 }} onPointerDown={(event) => event.stopPropagation()}>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: "#fff" }}>Remove Expert Rankings?</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.68)", lineHeight: 1.4 }}>
                  Are you sure you want to remove imported expert rankings?
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <button type="button" onClick={() => setConfirmRemoveExpertOpen(false)} style={pillStyle}>Cancel</button>
                <button
                  type="button"
                  onClick={() => {
                    onRemoveExpertRankings?.();
                    setConfirmRemoveExpertOpen(false);
                    setDesktopRankingsSettingsOpen(false);
                  }}
                  style={pillDangerStyle}
                >
                  Remove Expert
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function RankingsSearchSection(props: {
  q: string;
  setQ: (value: string) => void;
  activeTab: string;
  showDropdown: boolean;
  matches: SearchMatch[];
  activeMatchIndex: number;
  setActiveMatchIndex: React.Dispatch<React.SetStateAction<number>>;
  jumpToPlayer: (id: string) => void;
  playersById: Record<string, Player>;
  draftedIds: Set<string>;
  getColor: (pos: Position) => string;
  hideRankColumn: boolean;
  rankingsListKey: RankingsListKey;
  onSetAsRankings?: () => void;
  setAsRankingsFeedback: boolean;
  setSetAsRankingsFeedback: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const {
    q,
    setQ,
    activeTab,
    showDropdown,
    matches,
    activeMatchIndex,
    setActiveMatchIndex,
    jumpToPlayer,
    playersById,
    draftedIds,
    getColor,
    hideRankColumn,
    rankingsListKey,
    onSetAsRankings,
    setAsRankingsFeedback,
    setSetAsRankingsFeedback,
  } = props;

  return (
    <div style={{ position: "relative", marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          value={q}
          onChange={(event) => setQ(event.target.value)}
          placeholder={`Search ${activeTab}...`}
          style={{
            width: "100%",
            padding: "8px 10px",
            border: "1px solid var(--border-0)",
            borderRadius: 10,
            outline: "none",
            background: "var(--panel-bg)",
            color: "var(--text-0)",
            fontWeight: 700,
          }}
          onKeyDown={(event) => {
            if (!showDropdown) {
              if (event.key === "Escape") setQ("");
              return;
            }

            if (event.key === "ArrowDown") {
              event.preventDefault();
              setActiveMatchIndex((index) => Math.min(index + 1, matches.length - 1));
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setActiveMatchIndex((index) => Math.max(index - 1, 0));
            } else if (event.key === "Enter") {
              event.preventDefault();
              const pick = matches[activeMatchIndex];
              if (pick) jumpToPlayer(pick.id);
            } else if (event.key === "Escape") {
              event.preventDefault();
              setQ("");
            }
          }}
        />
        {rankingsListKey === "Experts" && onSetAsRankings ? (
          <button
            type="button"
            onClick={() => {
              onSetAsRankings();
              setSetAsRankingsFeedback(true);
            }}
            style={{
              flex: "0 0 auto",
              padding: "8px 12px",
              borderRadius: 10,
              border: setAsRankingsFeedback ? "1px solid rgba(255,255,255,0.24)" : "1px solid rgba(255,255,255,0.12)",
              background: setAsRankingsFeedback ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.05)",
              color: "var(--text-0)",
              fontSize: 11,
              fontWeight: 900,
              cursor: "pointer",
              whiteSpace: "nowrap",
              alignSelf: "stretch",
            }}
          >
            {setAsRankingsFeedback ? "Rankings Set!" : "Set as Rankings"}
          </button>
        ) : null}
      </div>

      {showDropdown ? (
        <div style={dropdownStyle}>
          <div style={dropdownLabelStyle}>Matches</div>
          {matches.map((match, index) => {
            const isActive = index === activeMatchIndex;
            const player = playersById[match.id];
            const pos = (player?.position ?? (match.pos as Position)) as Position;
            const drafted = draftedIds.has(match.id);
            const rowBg = drafted ? "var(--surface-0)" : getColor(pos);
            const rowFg = drafted ? "var(--text-1)" : "#000";

            return (
              <div
                key={match.id}
                onMouseEnter={() => setActiveMatchIndex(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => jumpToPlayer(match.id)}
                style={{
                  padding: "10px 12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  background: rowBg,
                  color: rowFg,
                  borderRadius: 12,
                  margin: "6px 6px 8px",
                  position: "relative",
                  userSelect: "none",
                  opacity: drafted ? 0.9 : 1,
                  border: isActive ? "2px solid rgba(34, 197, 94, 0.55)" : "1px solid rgba(255,255,255,0.10)",
                  boxShadow: isActive ? "0 0 0 2px rgba(0,0,0,0.12)" : "0 6px 14px rgba(0,0,0,0.18)",
                  transform: isActive ? "translateY(-1px)" : undefined,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                  <Headshot src={player?.imageUrl} alt={match.name} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 800, whiteSpace: "nowrap" }}>{match.name}</div>
                    <div style={{ fontSize: "clamp(10px, 2.2vw, 12px)", opacity: 0.8 }}>{pos}</div>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {drafted ? (
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 900,
                        letterSpacing: 0.4,
                        padding: "2px 8px",
                        borderRadius: 999,
                        background: "rgba(240, 234, 234, 0.18)",
                      }}
                    >
                      DRAFTED
                    </div>
                  ) : null}

                  <div style={{ fontSize: "clamp(10px, 2.2vw, 12px)", fontWeight: 900, opacity: 0.85 }}>
                    {hideRankColumn ? "\u00A0" : `#${match.rankLabel}`}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export function RankingsEmptyState(props: {
  rankingsListKey: RankingsListKey;
  expertInfoOpen: boolean;
  setExpertInfoOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onImportExpertRankings?: () => void;
}) {
  const { rankingsListKey, expertInfoOpen, setExpertInfoOpen, onImportExpertRankings } = props;

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        padding: "18px 14px",
        border: "1px solid rgba(255,255,255,0.10)",
        background: "rgba(0,0,0,0.78)",
        color: "rgba(255,255,255,0.82)",
        fontWeight: 800,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {rankingsListKey === "Experts" || rankingsListKey === "Rankings" ? (
        <div style={{ display: "grid", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: "var(--text-0)" }}>
              Import rankings from your favorite fantasy analysts
            </div>
            <button
              type="button"
              onClick={() => setExpertInfoOpen((open) => !open)}
              aria-label="Show expert import info"
              title="Show expert import info"
              style={{
                width: 24,
                height: 24,
                borderRadius: 999,
                border: "1px solid rgba(255,255,255,0.16)",
                background: expertInfoOpen ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.06)",
                color: "var(--text-0)",
                cursor: "pointer",
                fontWeight: 900,
                fontSize: 13,
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}
            >
              i
            </button>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <button
              type="button"
              onClick={() => onImportExpertRankings?.()}
              style={{
                padding: "8px 14px",
                borderRadius: 999,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.92)",
                color: "#0f172a",
                cursor: "pointer",
                fontWeight: 900,
              }}
            >
              Import
            </button>
          </div>
          {expertInfoOpen ? (
            <div
              style={{
                maxWidth: 460,
                padding: "12px 14px",
                borderRadius: 14,
                border: "1px solid rgba(255,255,255,0.10)",
                background: "rgba(255,255,255,0.06)",
                color: "rgba(255,255,255,0.82)",
                fontSize: 13,
                fontWeight: 700,
                lineHeight: 1.45,
              }}
            >
              Fantasy experts often offer fantasy rankings in the form of CSV downloads. Download those rankings and import directly to have a side by side comparison with your favorite analysts.
            </div>
          ) : null}
        </div>
      ) : (
        "No players to show. All players in this view are currently drafted."
      )}
    </div>
  );
}

const sectionLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  color: "rgba(255,255,255,0.68)",
  letterSpacing: 0.4,
  textTransform: "uppercase",
};

const pillStyle: React.CSSProperties = {
  flex: "0 0 auto",
  padding: "7px 11px",
  borderRadius: 999,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.05)",
  color: "var(--text-0)",
  fontSize: 11,
  fontWeight: 800,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const pillSelectedStyle: React.CSSProperties = {
  ...pillStyle,
  border: "1px solid rgba(255,255,255,0.24)",
  background: "rgba(255,255,255,0.14)",
};

const pillDangerStyle: React.CSSProperties = {
  ...pillStyle,
  border: "1px solid rgba(239,68,68,0.24)",
  background: "rgba(239,68,68,0.12)",
};

const closeButtonStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 999,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.06)",
  color: "var(--text-0)",
  cursor: "pointer",
};

const overlayStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  borderRadius: 16,
  background: "rgba(6,8,16,0.72)",
  backdropFilter: "blur(4px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
};

const confirmCardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 290,
  borderRadius: 16,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(12,18,34,0.98)",
  boxShadow: "0 18px 38px rgba(0,0,0,0.38)",
  padding: 14,
  display: "grid",
  gap: 12,
};

const dropdownStyle: React.CSSProperties = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  marginTop: 6,
  borderRadius: 14,
  zIndex: 80,
  maxHeight: 320,
  overflow: "auto",
  padding: 6,
  background: "rgba(15, 23, 42, 0.92)",
  border: "1px solid var(--border-1)",
  boxShadow: "0 18px 44px rgba(0,0,0,0.55)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  isolation: "isolate",
  outline: "1px solid rgba(34, 197, 94, 0.18)",
  outlineOffset: -1,
};

const dropdownLabelStyle: React.CSSProperties = {
  padding: "8px 10px 4px",
  fontSize: "clamp(10px, 2.2vw, 12px)",
  fontWeight: 900,
  letterSpacing: 0.7,
  textTransform: "uppercase",
  color: "var(--text-1)",
  opacity: 0.9,
  userSelect: "none",
};
